<template>
  <div data-locator="subscription-consumption-datarooms">
    <app-alert-in-page v-if="subscriptionDrPpuError"
                       :dismissible="false"
                       variant="error"
                       no-icon
    >
      {{ $t(`${TRANSLATION_KEY_PREFIX}.error`) }}
    </app-alert-in-page>

    <v-row v-else-if="subscriptionDrPpuPending" data-locator="subscription-consumption-datarooms-loading">
      <v-col v-for="n in SKELETON_CARDS_COUNT" :key="n" cols="12" md="6">
        <v-skeleton-loader type="heading, paragraph, actions" class="border"/>
      </v-col>
    </v-row>

    <template v-else-if="filteredDatarooms.length">
      <v-row>
        <v-col
          v-for="dataroom in paginatedDatarooms"
          :key="dataroom.id"
          cols="12"
          md="6"
        >
          <SubscriptionConsumptionDrPpuCard
            :dataroom="dataroom"
            @increase-storage="openDataroomUpgradeDialog"
          />
        </v-col>
      </v-row>

      <div class="d-flex justify-end mt-4">
        <AppPagination v-model:current-page="currentPage"
                       v-model:page-size="itemsPerPage"
                       :page-sizes="ITEMS_PER_PAGE"
                       :total="filteredDatarooms.length"
        />
      </div>
    </template>

    <SubscriptionConsumptionEmptyPanel
      v-else
      :icon="ROOM_ICONS.dataroom.name"
      :icon-color="ROOM_ICONS.dataroom.color"
      :title="$t(`${TRANSLATION_KEY_PREFIX}.${emptyStateKey}.title`)"
      :text="$t(`${TRANSLATION_KEY_PREFIX}.${emptyStateKey}.text`)"
      data-locator="subscription-consumption-datarooms-empty"
    />

    <ProjectSettingsDataroomUpgradeDialog
      v-if="selectedDataroom"
      :room="selectedDataroomRoom"
      :submit-action="increaseDataroomStorage"
      :submit-loading="subscriptionDrPpuStorageAddonPending"
      @close="selectedDataroom = null"
      @refresh-stats="refreshDataroomsPayPerUse"
    />
  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import { mapState, mapActions, useStore } from 'vuex'

  import AppPagination from '@/common/pagination/AppPagination.vue'
  import { usePagination } from '@/common/pagination/usePagination'
  import { ROOM_ICONS } from '@/common/utils/rooms'
  import { toast } from '@/common/utils/toast'
  import ProjectSettingsDataroomUpgradeDialog from '@/project/project-settings/ProjectSettingsDataroomUpgradeDialog.vue'
  import { GET_SUBSCRIPTION_DR_PPU, GET_SUBSCRIPTION_DR_PPU_HISTORY, POST_SUBSCRIPTION_DR_PPU_STORAGE_ADDON } from '@/store/modules/subscription/action_types'
  import { SUBSCRIPTION_DR_PPU_ITEMS_PER_PAGE } from '@/subscriptions/constants.js'
  import SubscriptionConsumptionDrPpuCard from '@/subscriptions/views/SubscriptionConsumptionDrPpuCard.vue'
  import SubscriptionConsumptionEmptyPanel from '@/subscriptions/views/SubscriptionConsumptionEmptyPanel.vue'
  import type { SubscriptionDrPpuDataroom } from '@/types/subscription'

  const TRANSLATION_KEY_PREFIX = 'subscriptions.views.SubscriptionConsumption.Datarooms'
  const ITEMS_PER_PAGE = [10, 25, 50]
  const SKELETON_CARDS_COUNT = 4

  export default defineComponent({
    name: 'SubscriptionConsumptionDrPpuUsage',
    components: { AppPagination, ProjectSettingsDataroomUpgradeDialog, SubscriptionConsumptionDrPpuCard, SubscriptionConsumptionEmptyPanel },
    props: {
      search: {
        type: String,
        default: '',
      },
    },
    setup () {
      const store = useStore()
      const { currentPage, itemsPerPage } = usePagination({
        currentUserId: store.getters['user/currentUserId'],
        userSettingsKey: SUBSCRIPTION_DR_PPU_ITEMS_PER_PAGE,
        pageSize: ITEMS_PER_PAGE[0],
      })

      return { currentPage, itemsPerPage }
    },
    data () {
      return {
        TRANSLATION_KEY_PREFIX,
        ITEMS_PER_PAGE,
        SKELETON_CARDS_COUNT,
        ROOM_ICONS,
        selectedDataroom: null as SubscriptionDrPpuDataroom | null,
      }
    },
    computed: {
      ...mapState('subscription', [
        'subscriptionDrPpu',
        'subscriptionDrPpuPending',
        'subscriptionDrPpuError',
        'subscriptionDrPpuStorageAddonPending',
      ]),
      hasSearch (): boolean {
        return this.search.trim().length > 0
      },
      emptyStateKey (): string {
        return this.hasSearch ? 'noResults' : 'emptyState'
      },
      filteredDatarooms (): SubscriptionDrPpuDataroom[] {
        const datarooms = (this.subscriptionDrPpu ?? []) as SubscriptionDrPpuDataroom[]
        if (!this.hasSearch) {
          return datarooms
        }
        const query = this.search.trim().toLowerCase()
        return datarooms.filter(dataroom => dataroom.name.toLowerCase().includes(query))
      },
      paginatedDatarooms (): SubscriptionDrPpuDataroom[] {
        const start = (this.currentPage - 1) * this.itemsPerPage
        return this.filteredDatarooms.slice(start, start + this.itemsPerPage)
      },
      selectedDataroomRoom () {
        const dataroom = this.selectedDataroom as SubscriptionDrPpuDataroom
        return {
          id: dataroom.id,
          mnemo: dataroom.mnemo,
          name: dataroom.name,
          createdAt: dataroom.createdAt,
          dataroomInfo: {
            initialStorageGb: dataroom.initialStorageGb,
            initialDuration: dataroom.initialDuration,
            monthlyPrice: dataroom.monthlyPrice,
            addonPrice: dataroom.addonPrice,
            addonPriceWithDiscount: dataroom.addonPriceWithDiscount,
            addonStorage: dataroom.addonStorage,
            addOns: dataroom.addOns,
          },
        }
      },
    },
    watch: {
      search () {
        this.currentPage = 1
      },
    },
    created () {
      this.getSubscriptionDataroomsPayPerUse()
    },
    methods: {
      ...mapActions('subscription', [GET_SUBSCRIPTION_DR_PPU, GET_SUBSCRIPTION_DR_PPU_HISTORY, POST_SUBSCRIPTION_DR_PPU_STORAGE_ADDON]),
      async getSubscriptionDataroomsPayPerUse () {
        try {
          await this.GET_SUBSCRIPTION_DR_PPU()
        } catch {
          toast.error(this.$t(`${TRANSLATION_KEY_PREFIX}.error`))
        }
      },
      openDataroomUpgradeDialog (dataroom: SubscriptionDrPpuDataroom) {
        this.selectedDataroom = dataroom
      },
      async increaseDataroomStorage (data: { storage: string, unit: string }) {
        if (!this.selectedDataroom) {
          return
        }
        await this.POST_SUBSCRIPTION_DR_PPU_STORAGE_ADDON({
          roomId: this.selectedDataroom.id,
          data,
        })
      },
      async refreshDataroomsPayPerUse () {
        this.selectedDataroom = null
        await this.getSubscriptionDataroomsPayPerUse()
        await this.GET_SUBSCRIPTION_DR_PPU_HISTORY()
      },
    },
  })
</script>
